import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send reset link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen grain-overlay">
      <SiteHeader />
      <main className="max-w-md mx-auto px-6 py-16">
        <p className="label-eyebrow">Forgot password</p>
        <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">
          Reset it in a minute.
        </h1>

        {sent ? (
          <div
            data-testid="forgot-password-success"
            className="mt-8 rounded-2xl border border-[#7A8B76]/40 bg-[#7A8B76]/10 p-6"
          >
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#7A8B76] text-white">
              <MailCheck className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <p className="mt-4 font-serif-display text-2xl">Check your inbox.</p>
            <p className="mt-2 text-sm text-[#4A4341]">
              If <span className="font-mono-jet">{email}</span> is on file, a password reset link
              is on its way. The link expires in 30 minutes.
            </p>
            <Link
              to="/signin"
              data-testid="forgot-back-to-signin"
              className="mt-6 inline-flex items-center gap-2 text-sm text-[#C05A46] hover:underline"
            >
              Back to sign in <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm text-[#4A4341]">
              Enter the email on your account and we&rsquo;ll send you a link to pick a new password.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="forgot-submit-btn"
                className="btn-brand w-full rounded-full py-6 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                {submitting ? "Sending…" : "Send reset link"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
            <div className="mt-8 text-sm">
              <Link
                to="/signin"
                data-testid="forgot-back-link"
                className="text-[#7B7573] hover:text-[#1A1514] transition-colors"
              >
                ← Back to sign in
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
